import {Checkbox} from '@wandb/components';
import React from 'react';
import {Popup} from 'semantic-ui-react';

import {recordRumAction} from '../../../util/datadog/datadogRum';
import {
  type RunToggleState,
  useRunTogglePaintVital,
} from '../../../util/datadog/workspace/runToggle';
import {useRunSelectorFeatureDisabled} from '../RunSelectorFeaturesContext';

type RowVisibilityProps = {
  isHiddenByParallelCoordinatesOrScatterPlot: boolean;
  isSingleMode: boolean;
  onChangeVisibility: React.MouseEventHandler;
  visibilityState: RunToggleState;
};

export function RowVisibility({
  isHiddenByParallelCoordinatesOrScatterPlot,
  isSingleMode,
  onChangeVisibility,
  visibilityState,
}: RowVisibilityProps) {
  const isDisabled = useRunSelectorFeatureDisabled();
  const startPaintVital = useRunTogglePaintVital(visibilityState);

  if (isSingleMode || isDisabled('toggle-visibility')) {
    return null;
  }

  const onCheckboxClick: React.MouseEventHandler = event => {
    const action = visibilityState === 'checked' ? 'hide run' : 'show run';
    startPaintVital({
      action,
      prevState: visibilityState,
    });
    onChangeVisibility(event);
    recordRumAction('workspace_run_toggled', {
      // 'checked' means the run was visible, so this click hides it
      action,
      prevState: visibilityState,
    });
  };

  const checkbox = (
    <Checkbox
      data-test="run-visibility-checkbox"
      isSelected={visibilityState === 'checked'}
      isIndeterminate={visibilityState === 'partial'}
      aria-label={visibilityState === 'checked' ? 'Hide run' : 'Show run'}
      variant="visibility"
      onClick={onCheckboxClick}
    />
  );

  if (isHiddenByParallelCoordinatesOrScatterPlot) {
    return (
      <Popup
        inverted
        position="right center"
        content="This run is hidden by a parallel coordinates or scatter plot. Deselect the range on that chart first."
        size="mini"
        on="hover"
        trigger={checkbox}
        offset={-3}
        popperModifiers={{
          preventOverflow: {
            // prevent popper from erroneously constraining the popup to the
            // table header
            boundariesElement: 'viewport',
          },
        }}
      />
    );
  }

  return checkbox;
}
